import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router, CanActivate } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { TokenValidationService } from '../Services/token-validation.service';
import { TokenEncryptionService } from '../Services/token-encryption.service';
import { clearStoragePreservingAttribution } from './attribution.util';

@Injectable({
  providedIn: 'root'
})
export class LoginGuard implements CanActivate {
  constructor(
    private router: Router,
    private tokenValidation: TokenValidationService,
    private tokenEncryption: TokenEncryptionService
  ) {
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const token = this.tokenEncryption.getToken();
    const isLoggedIn = localStorage.getItem('isLogedIn') === 'true';

    // If user is not logged in, redirect to login
    if (!token || !isLoggedIn) {
      return this.redirectToLogin(state.url);
    }

    // If token is expired, clear session and redirect
    if (this.tokenValidation.isTokenExpired(token)) {
      this.clearSession();
      return this.redirectToLogin(state.url);
    }

    // Validate token with backend
    return this.tokenValidation.validateToken(token).pipe(
      map(valid => {
        if (valid) {
          return true;
        } else {
          this.clearSession();
          return this.redirectToLogin(state.url);
        }
      }),
      catchError(() => {
        this.clearSession();
        return of(this.redirectToLogin(state.url));
      })
    );
  }

  private redirectToLogin(returnUrl: string): UrlTree {
    return this.router.createUrlTree(['/login'], { queryParams: { returnUrl } });
  }

  private clearSession(): void {
    clearStoragePreservingAttribution(localStorage);
    sessionStorage.clear();
  }
}
